import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../../../firebase";
import {
  COL_EXPENSE_BRANCH_TRANSFER_JOURNALS,
  PARTY_WINS_AUTOS_MAIN,
  PARTY_WINS_BRANCH_SDEBTOR,
  VOUCHER_JOURNAL,
  VOUCHER_RECEIPT,
  VOUCHER_TRANSFER_IN,
  VOUCHER_TRANSFER_OUT_NEW,
} from "../constants";
import { salesImportMonthDocId } from "../../salesDataImport/utils/weekUtils";
import {
  isTransferInVoucherRow,
  isTransferOutNewVoucherRow,
  isWinsBranchSDebtorParty,
} from "../../../services/salesImportTransactionDataService";
import {
  normalizeSalesImportBranchId,
  salesImportBranchDisplayName,
  SALES_IMPORT_BRANCH_NAMES,
} from "../../salesDataImport/config/salesImportBranchIds";
import { FY_MONTH_ORDER_SHORT } from "../../../utils/financialYear";

const MAIN_BRANCH_ID = "1";
const BRANCH_IDS = ["1", "10", "11"];

function normVoucherType(row) {
  return String(row?.voucherTypeName ?? row?.voucherType ?? "").trim();
}

function normTxnType(row) {
  return String(row?.transactionType ?? row?.transactionTyp ?? "").trim();
}

function typeBlob(row) {
  return `${normVoucherType(row)} ${normTxnType(row)}`.toUpperCase();
}

function rowAmount(row) {
  const amt = Math.abs(Number(row.amount) || 0);
  return amt > 0 ? amt : 0;
}

export function isWinsAutosMainParty(row) {
  const name = String(row?.partyName ?? row?.party ?? row?.customerName ?? "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!name) return false;
  const compact = name.replace(/[\s.\-_/()]/g, "");
  return (
    name.includes("WINS") &&
    name.includes("AUTOS") &&
    (name.includes("MAIN") || compact.includes("WINSAUTOSMAIN"))
  );
}

export function isReceiptVoucherRow(row) {
  const blob = typeBlob(row);
  if (blob.includes("JOURNAL")) return false;
  return /\bRECEIPT\b/.test(blob) || blob.includes("RECEIPT");
}

export function isJournalVoucherRow(row) {
  const blob = typeBlob(row);
  if (blob.includes("RECEIPT")) return false;
  return /\bJOURNAL\b/.test(blob) || blob.includes("JOURNAL");
}

/** A. Main → Branch: Transfer Out - New · WINS BRANCH (S.DEBTOR) · Branch ID 1 */
export function isMainToBranchTransferRow(row) {
  if (normalizeSalesImportBranchId(row.branchId) !== MAIN_BRANCH_ID) return false;
  return (
    isTransferOutNewVoucherRow({ voucherType: normVoucherType(row) }) &&
    isWinsBranchSDebtorParty({ partyName: row.partyName, customerName: row.partyName })
  );
}

export function isCreditTransactionRow(row) {
  const tt = normTxnType(row).toUpperCase();
  if (!tt) return true;
  return (
    tt === "CREDIT" ||
    tt === "CR" ||
    tt === "C" ||
    tt.startsWith("CREDIT") ||
    /\bCR\b/.test(tt)
  );
}

/** B. Branch → Main: Transfer IN · WINS AUTOS MAIN · Branch ID 1 · Credit */
export function isBranchToMainTransferRow(row) {
  if (normalizeSalesImportBranchId(row.branchId) !== MAIN_BRANCH_ID) return false;
  if (!isTransferInVoucherRow({ voucherType: normVoucherType(row) })) return false;
  if (!isWinsAutosMainParty(row)) return false;
  return isCreditTransactionRow(row);
}

/** C1. Collection — Receipt · WINS BRANCH (S.DEBTOR) · any branch */
export function isBranchCollectionReceiptRow(row) {
  if (!isReceiptVoucherRow(row)) return false;
  return isWinsBranchSDebtorParty({ partyName: row.partyName, customerName: row.partyName });
}

/** C2. Collection via Journal · WINS BRANCH (S.DEBTOR) (any branch, incl. Main settlement). */
export function isBranchCollectionJournalRow(row) {
  return (
    isJournalVoucherRow(row) &&
    isWinsBranchSDebtorParty({ partyName: row.partyName, customerName: row.partyName })
  );
}

/** Collections = Receipt + Journal (WINS BRANCH S.DEBTOR). */
export function isBranchCollectionRow(row) {
  return isBranchCollectionReceiptRow(row) || isBranchCollectionJournalRow(row);
}

/** Payment · WINS BRANCH (S.DEBTOR) — amount counted under Journal (GST). */
export function isBranchPaymentGstRow(row) {
  const blob = typeBlob(row);
  if (!blob.includes("PAYMENT")) return false;
  if (blob.includes("RECEIPT") || blob.includes("JOURNAL")) return false;
  return isWinsBranchSDebtorParty({ partyName: row.partyName, customerName: row.partyName });
}

/** Dr/Cr side from Transaction Type; falls back to amount sign (negative = credit). */
export function transferRowSide(row) {
  const tt = normTxnType(row).toUpperCase();
  if (tt.startsWith("DEBIT") || tt === "DR" || tt === "D" || /\bDR\b/.test(tt)) return "debit";
  if (tt.startsWith("CREDIT") || tt === "CR" || tt === "C" || /\bCR\b/.test(tt)) return "credit";
  return (Number(row.amount) || 0) < 0 ? "credit" : "debit";
}

export function enrichTransferLine(row) {
  const amountAbs = rowAmount(row);
  const side = transferRowSide(row);
  return {
    ...row,
    amountAbs,
    debit: side === "debit" ? amountAbs : 0,
    credit: side === "credit" ? amountAbs : 0,
    branchLabel: salesImportBranchDisplayName(row.branchId),
    voucherTypeName: normVoucherType(row) || row.voucherTypeName,
    partyName: String(row.partyName || "").trim(),
  };
}

function sortLines(a, b) {
  return (
    String(a.tranDate).localeCompare(String(b.tranDate)) ||
    String(a.voucherNo).localeCompare(String(b.voucherNo))
  );
}

function classifyMonthRows(monthRows) {
  const mainToBranch = [];
  const branchToMain = [];
  const collections = [];
  const collectionReceipts = [];
  const collectionJournals = [];

  for (const row of monthRows) {
    const line = enrichTransferLine(row);
    if (isMainToBranchTransferRow(row)) mainToBranch.push(line);
    else if (isBranchToMainTransferRow(row)) branchToMain.push(line);
    else if (isBranchCollectionReceiptRow(row)) {
      collections.push(line);
      collectionReceipts.push(line);
    } else if (isBranchCollectionJournalRow(row)) {
      collections.push(line);
      collectionJournals.push(line);
    } else if (isBranchPaymentGstRow(row)) {
      // Payment vouchers always count on the GST (debit) side.
      const payLine = { ...line, debit: line.amountAbs, credit: 0 };
      collections.push(payLine);
      collectionJournals.push(payLine);
    }
  }

  mainToBranch.sort(sortLines);
  branchToMain.sort(sortLines);
  collections.sort(sortLines);
  collectionReceipts.sort(sortLines);
  collectionJournals.sort(sortLines);

  return { mainToBranch, branchToMain, collections, collectionReceipts, collectionJournals };
}

function filterLinesByBranch(lists, viewBranchId) {
  if (!viewBranchId || viewBranchId === "all") return lists;
  const keep = (row) => normalizeSalesImportBranchId(row.branchId) === viewBranchId;
  return {
    mainToBranch: lists.mainToBranch.filter(keep),
    branchToMain: lists.branchToMain.filter(keep),
    collections: lists.collections.filter(keep),
    collectionReceipts: lists.collectionReceipts.filter(keep),
    collectionJournals: lists.collectionJournals.filter(keep),
  };
}

export function buildMonthBranchTransferReport(rows, { calendarYear, monthIndex, viewBranchId = "all" }) {
  const monthRows = (rows || []).filter(
    (r) => r.calendarYear === calendarYear && r.monthIndex === monthIndex
  );

  const all = classifyMonthRows(monthRows);
  const filtered = filterLinesByBranch(all, viewBranchId);

  const sum = (list) => list.reduce((s, r) => s + r.amountAbs, 0);

  // Company settlement uses full-month totals (Main transfer out vs all branch in/collections).
  const transferOutTotal = sum(all.mainToBranch);
  const transferInTotal = sum(all.branchToMain);
  const collectionsReceiptTotal = sum(all.collectionReceipts);
  const collectionsJournalDebitTotal = all.collectionJournals.reduce((s, r) => s + (r.debit || 0), 0);
  const collectionsJournalCreditTotal = all.collectionJournals.reduce((s, r) => s + (r.credit || 0), 0);
  const collectionsJournalTotal = collectionsJournalDebitTotal + collectionsJournalCreditTotal;
  // Collections = Receipt + Journal (collection/credit). Journal (GST/debit) adds to the out side.
  const collectionsTotal = collectionsReceiptTotal + collectionsJournalCreditTotal;
  // Pending = Transfer out + Journal (GST) − Transfer in − Receipt
  const pendingBalance =
    transferOutTotal + collectionsJournalDebitTotal - transferInTotal - collectionsReceiptTotal;

  return {
    ...filtered,
    summary: {
      transferOutTotal,
      transferInTotal,
      collectionsReceiptTotal,
      collectionsJournalDebitTotal,
      collectionsJournalCreditTotal,
      collectionsJournalTotal,
      collectionsTotal,
      pendingBalance,
      mainToBranchCount: filtered.mainToBranch.length,
      branchToMainCount: filtered.branchToMain.length,
      collectionsCount: filtered.collections.length,
      collectionsReceiptCount: filtered.collectionReceipts.length,
      collectionsJournalCount: filtered.collectionJournals.length,
    },
  };
}

/** FY month-wise summary (Main branch transfer out is company-wide; in/out/collections all branches). */
export function buildFyTransferMonthSummary(rows, viewBranchId = "all") {
  const bySlot = new Map();

  for (const row of rows || []) {
    const slot = Number(row.fySlot);
    if (!Number.isFinite(slot)) continue;
    const bid = normalizeSalesImportBranchId(row.branchId);
    if (viewBranchId !== "all" && bid !== viewBranchId) continue;

    const curr = bySlot.get(slot) || {
      fySlot: slot,
      fyMonthLabel: row.fyMonthLabel || FY_MONTH_ORDER_SHORT[slot] || "",
      calendarYear: row.calendarYear,
      monthIndex: row.monthIndex,
      transferOut: 0,
      transferIn: 0,
      collections: 0,
      collectionsReceipt: 0,
      collectionsJournal: 0,
      collectionsJournalDebit: 0,
      collectionsJournalCredit: 0,
      pending: 0,
    };

    const amt = rowAmount(row);
    if (isMainToBranchTransferRow(row) && (viewBranchId === "all" || bid === viewBranchId)) {
      curr.transferOut += amt;
    }
    if (isBranchToMainTransferRow(row)) curr.transferIn += amt;
    if (isBranchCollectionReceiptRow(row)) {
      curr.collectionsReceipt += amt;
      curr.collections += amt;
    } else if (isBranchCollectionJournalRow(row)) {
      if (transferRowSide(row) === "credit") {
        curr.collectionsJournalCredit += amt;
        curr.collections += amt;
      } else {
        curr.collectionsJournalDebit += amt;
      }
      curr.collectionsJournal += amt;
    } else if (isBranchPaymentGstRow(row)) {
      curr.collectionsJournalDebit += amt;
      curr.collectionsJournal += amt;
    }

    bySlot.set(slot, curr);
  }

  const months = [...bySlot.values()]
    .sort((a, b) => a.fySlot - b.fySlot)
    .map((m) => ({
      ...m,
      pending: m.transferOut + m.collectionsJournalDebit - m.transferIn - m.collectionsReceipt,
    }));

  const totals = months.reduce(
    (acc, m) => ({
      transferOut: acc.transferOut + m.transferOut,
      transferIn: acc.transferIn + m.transferIn,
      collections: acc.collections + m.collections,
      collectionsReceipt: acc.collectionsReceipt + (m.collectionsReceipt || 0),
      collectionsJournal: acc.collectionsJournal + (m.collectionsJournal || 0),
      collectionsJournalDebit: acc.collectionsJournalDebit + (m.collectionsJournalDebit || 0),
      collectionsJournalCredit: acc.collectionsJournalCredit + (m.collectionsJournalCredit || 0),
      pending: acc.pending + m.pending,
    }),
    {
      transferOut: 0,
      transferIn: 0,
      collections: 0,
      collectionsReceipt: 0,
      collectionsJournal: 0,
      collectionsJournalDebit: 0,
      collectionsJournalCredit: 0,
      pending: 0,
    }
  );

  return { months, totals };
}

/** Branch-wise breakdown for selected month. */
export function buildBranchWiseTransferReport(rows, { calendarYear, monthIndex }) {
  const monthRows = (rows || []).filter(
    (r) => r.calendarYear === calendarYear && r.monthIndex === monthIndex
  );

  const byBranch = new Map();

  const ensure = (bid) => {
    if (!byBranch.has(bid)) {
      byBranch.set(bid, {
        branchId: bid,
        branchLabel: salesImportBranchDisplayName(bid),
        transferOut: 0,
        transferIn: 0,
        collections: 0,
        collectionsReceipt: 0,
        collectionsJournal: 0,
        collectionsJournalDebit: 0,
        collectionsJournalCredit: 0,
        pending: 0,
        lines: 0,
      });
    }
    return byBranch.get(bid);
  };

  for (const row of monthRows) {
    const bid = normalizeSalesImportBranchId(row.branchId) || "Unknown";
    const bucket = ensure(bid);
    const amt = rowAmount(row);
    bucket.lines += 1;

    if (isMainToBranchTransferRow(row)) bucket.transferOut += amt;
    if (isBranchToMainTransferRow(row)) bucket.transferIn += amt;
    if (isBranchCollectionReceiptRow(row)) {
      bucket.collectionsReceipt += amt;
      bucket.collections += amt;
    } else if (isBranchCollectionJournalRow(row)) {
      if (transferRowSide(row) === "credit") {
        bucket.collectionsJournalCredit += amt;
        bucket.collections += amt;
      } else {
        bucket.collectionsJournalDebit += amt;
      }
      bucket.collectionsJournal += amt;
    } else if (isBranchPaymentGstRow(row)) {
      bucket.collectionsJournalDebit += amt;
      bucket.collectionsJournal += amt;
    }
  }

  const branches = [...byBranch.values()]
    .map((b) => ({
      ...b,
      pending: b.transferOut + b.collectionsJournalDebit - b.transferIn - b.collectionsReceipt,
    }))
    .sort((a, b) => {
      const ia = BRANCH_IDS.indexOf(a.branchId);
      const ib = BRANCH_IDS.indexOf(b.branchId);
      if (ia >= 0 && ib >= 0) return ia - ib;
      return a.branchLabel.localeCompare(b.branchLabel);
    });

  return branches;
}

export function buildPendingSettlementReport(fyMonthSummary) {
  return (fyMonthSummary.months || []).filter((m) => Math.abs(m.pending) > 0.01);
}

export function journalDocId(calendarYear, monthIndex) {
  return salesImportMonthDocId(calendarYear, monthIndex);
}

export async function loadSettlementJournal(calendarYear, monthIndex) {
  const id = journalDocId(calendarYear, monthIndex);
  const snap = await getDoc(doc(db, COL_EXPENSE_BRANCH_TRANSFER_JOURNALS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function loadSettlementJournalsForYear(calendarYears) {
  const years = [...new Set(calendarYears)].filter(Boolean);
  if (!years.length) return [];
  const col = collection(db, COL_EXPENSE_BRANCH_TRANSFER_JOURNALS);
  const out = [];
  for (const y of years) {
    const q = query(col, where("calendarYear", "==", Number(y)));
    const snap = await getDocs(q);
    snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  }
  return out.sort((a, b) => String(b.monthId).localeCompare(String(a.monthId)));
}

export async function createSettlementJournal({
  calendarYear,
  monthIndex,
  transferOutTotal,
  transferInTotal,
  collectionsTotal,
  pendingBalance,
  createdBy,
  note = "",
}) {
  const monthId = journalDocId(calendarYear, monthIndex);
  const ref = doc(db, COL_EXPENSE_BRANCH_TRANSFER_JOURNALS, monthId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(`Journal entry already exists for ${monthId}. Duplicate month-end journals are not allowed.`);
  }

  if (!(Math.abs(pendingBalance) > 0.001)) {
    throw new Error("Pending balance is zero — no journal entry required for this month.");
  }

  const payload = {
    monthId,
    calendarYear: Number(calendarYear),
    monthIndex: Number(monthIndex),
    voucherType: VOUCHER_JOURNAL,
    partyName: PARTY_WINS_BRANCH_SDEBTOR,
    transferOutTotal: Number(transferOutTotal) || 0,
    transferInTotal: Number(transferInTotal) || 0,
    collectionsTotal: Number(collectionsTotal) || 0,
    pendingBalance: Number(pendingBalance) || 0,
    journalAmount: Number(pendingBalance) || 0,
    status: "posted",
    purpose: "Month-end settlement of Main Branch Transfer Out amounts",
    note: String(note || "").trim(),
    createdBy: createdBy || "",
    createdAt: serverTimestamp(),
    lastModifiedBy: createdBy || "",
    lastModifiedAt: serverTimestamp(),
  };

  await setDoc(ref, payload);
  return payload;
}

export const TRANSFER_RULES = [
  {
    id: "main-to-branch",
    title: "Main → Branch",
    voucherType: VOUCHER_TRANSFER_OUT_NEW,
    party: PARTY_WINS_BRANCH_SDEBTOR,
    branch: "Main (Branch ID 1)",
  },
  {
    id: "branch-to-main",
    title: "Branch → Main",
    voucherType: VOUCHER_TRANSFER_IN,
    party: PARTY_WINS_AUTOS_MAIN,
    branch: "Main (Branch ID 1) · Transaction type Credit",
  },
  {
    id: "collection",
    title: "Collections",
    voucherType: `${VOUCHER_RECEIPT} + ${VOUCHER_JOURNAL} (Payment → GST)`,
    party: PARTY_WINS_BRANCH_SDEBTOR,
    branch: "Any branch · WINS BRANCH (S.DEBTOR)",
  },
  {
    id: "journal",
    title: "App month-end journal",
    voucherType: VOUCHER_JOURNAL,
    party: PARTY_WINS_BRANCH_SDEBTOR,
    branch: "Posted from this page (audit)",
  },
];

export { SALES_IMPORT_BRANCH_NAMES, MAIN_BRANCH_ID };
